import { useMemo } from "react";

import { formatMoneyCompact, formatNumber, formatPeriod } from "@/lib/utils/format";
import { can } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";
import { RecentActivities } from "../../RecentActivities";
import { HiringTrendChart } from "../../HiringTrendChart";
import { DepartmentDonut } from "../../DepartmentDonut";
import { BranchDonut } from "../../BranchDonut";
import { AttendanceDonut } from "../../AttendanceDonut";
import { buildHiringTrend } from "../../hiring-trend";
import {
  EmployeeListCard,
  EmployeeStatusCard,
  GeoSnapshotCard,
  LeaveApplicationsCard,
  PayrollSnapshotCard,
  PulseCard,
  WorkforceInsightsCard,
} from "../../sections";
import { computeTrend, startOfMonthISO } from "../../sections-utils";
import {
  useBranches,
  useDashboardReport,
  useDepartments,
  useDepartmentSummary,
  useExpenseSummary,
  useGeoSnapshot,
  useHeadcount,
  useHrToday,
  useRecentActivity,
  useRecentJoiners,
  useRecentLeaves,
} from "./data";
import { KpiWidget, SummaryWidget, ViewAllLink, WidgetCard, type WidgetProps } from "./kit";
import { combineStates, type DataState } from "./state";

// ---------------------------------------------------------------- shared

/** A query that was switched off (no permission / module) has nothing to
 * show and is not loading: the widget shows its empty state, never a spinner
 * that never ends. */
function stateOf(q: { isLoading: boolean; isError: boolean; fetchStatus: string; refetch: () => unknown }): DataState {
  return { isLoading: q.isLoading && q.fetchStatus !== "idle", isError: q.isError, refetch: q.refetch };
}

function NoAccess({ what }: { what: string }) {
  return <p className="py-6 text-center text-sm text-slate-400">{what} isn&apos;t available for your access.</p>;
}

// ------------------------------------------------------- employee overview

export function TotalEmployeesWidget(_: WidgetProps) {
  const q = useDashboardReport();
  const current = q.data?.current;
  return (
    <KpiWidget
      label="Total Employees"
      icon="users"
      tone="primary"
      to="/employees"
      state={stateOf(q)}
      value={current ? formatNumber(current.headcount) : undefined}
      hint="on-roll"
      trend={current ? computeTrend(current.headcount, q.data?.previous?.headcount) : undefined}
    />
  );
}

export function NewJoinersWidget(_: WidgetProps) {
  const q = useRecentJoiners();
  const joined = q.data?.items.filter((e) => e.joining_date >= startOfMonthISO()).length;
  return (
    <KpiWidget label="New joiners" icon="users" tone="emerald" to="/employees" state={stateOf(q)} value={joined === undefined ? undefined : formatNumber(joined)} hint="this month" />
  );
}

export function EmployeeStatusWidget(_: WidgetProps) {
  const q = useHeadcount();
  return (
    <div className="h-full [&>div]:h-full">
      {q.isLoading || q.isError ? (
        <WidgetCard title="Employee Status" state={stateOf(q)}>
          {null}
        </WidgetCard>
      ) : (
        <EmployeeStatusCard byStatus={q.data?.by_status} className="h-full" />
      )}
    </div>
  );
}

export function HiringTrendWidget(_: WidgetProps) {
  const q = useRecentJoiners();
  const data = useMemo(() => buildHiringTrend(q.data?.items ?? []), [q.data]);
  return (
    <WidgetCard title="Hiring Trend" state={stateOf(q)} action={<ViewAllLink to="/employees" />}>
      <HiringTrendChart data={data} />
    </WidgetCard>
  );
}

export function DepartmentsWidget(_: WidgetProps) {
  const q = useDepartments();
  return (
    <WidgetCard title="Employees by Department" state={stateOf(q)} action={<ViewAllLink to="/departments" />}>
      <DepartmentDonut departments={q.data?.items ?? []} />
    </WidgetCard>
  );
}

export function BranchesWidget(_: WidgetProps) {
  const q = useBranches();
  const branchesEnabled = useFeatureEnabled("branch_management");
  return (
    <WidgetCard title="Employees by Branch" state={stateOf(q)} action={branchesEnabled ? <ViewAllLink to="/branches" /> : undefined}>
      <BranchDonut branches={q.data?.items ?? []} />
    </WidgetCard>
  );
}

export function EmployeeDirectoryWidget(_: WidgetProps) {
  const q = useRecentJoiners();
  if (q.isLoading || q.isError) {
    return (
      <WidgetCard title="Employee List" state={stateOf(q)}>
        {null}
      </WidgetCard>
    );
  }
  return (
    <div className="h-full min-w-0 overflow-x-auto [&>div]:h-full">
      <EmployeeListCard items={q.data?.items} className="h-full" />
    </div>
  );
}

/** The original dashboard's tabbed Hiring Trend / Departments / Attendance /
 * Branches card. */
export function WorkforceInsightsWidget(_: WidgetProps) {
  const report = useDashboardReport();
  const joiners = useRecentJoiners();
  const departments = useDepartments();
  const summary = useDepartmentSummary();
  const branches = useBranches();
  const employeeMgmt = useFeatureEnabled("employee_management");
  const attendance = useFeatureEnabled("attendance_management");
  const directory = employeeMgmt && can("employee.read");
  const branchesEnabled = useFeatureEnabled("branch_management");
  const hiringTrend = useMemo(() => buildHiringTrend(joiners.data?.items ?? []), [joiners.data]);

  const tabs = useMemo(() => {
    const t: { key: string; label: string }[] = [];
    if (directory) t.push({ key: "hiring", label: "Hiring Trend" }, { key: "departments", label: "Departments" });
    if (attendance) t.push({ key: "attendance", label: "Attendance" });
    if (branchesEnabled && can("branch.read")) t.push({ key: "branches", label: "Branches" });
    return t;
  }, [directory, attendance, branchesEnabled]);

  const state = combineStates(stateOf(report), stateOf(joiners), stateOf(departments), stateOf(summary), stateOf(branches));
  if (state.isLoading || state.isError || tabs.length === 0) {
    return (
      <WidgetCard title="Workforce Insights" state={state}>
        <NoAccess what="Workforce insights" />
      </WidgetCard>
    );
  }
  return (
    <div className="h-full min-w-0 [&>div]:h-full">
      <WorkforceInsightsCard
        className="h-full"
        tabs={tabs}
        hiringTrend={hiringTrend}
        departments={departments.data?.items ?? []}
        departmentSummary={summary.data}
        attendanceMix={report.data?.current.attendance_mix ?? {}}
        branches={branches.data?.items ?? []}
      />
    </div>
  );
}

/** The original dashboard's compact "pulse" strip. */
export function WorkforcePulseWidget(_: WidgetProps) {
  const report = useDashboardReport();
  const joiners = useRecentJoiners();
  const attendance = useFeatureEnabled("attendance_management");
  const leave = useFeatureEnabled("leave_management");
  const employeeMgmt = useFeatureEnabled("employee_management");
  const directory = employeeMgmt && can("employee.read");
  const state = combineStates(stateOf(report), stateOf(joiners));
  const current = report.data?.current;
  if (state.isLoading || state.isError || !current) {
    return (
      <WidgetCard title="Workforce at a Glance" state={state} minBodyHeight="min-h-[5rem]">
        <NoAccess what="Workforce figures" />
      </WidgetCard>
    );
  }
  const onLeave = current.attendance_mix?.leave ?? 0;
  const newJoinees = joiners.data?.items.filter((e) => e.joining_date >= startOfMonthISO()).length ?? 0;
  return (
    <div className="h-full [&>div]:h-full">
      <PulseCard
        stats={[
          ...(attendance || leave
            ? [{ key: "on-leave", label: "On leave", value: formatNumber(onLeave), hint: current.headcount ? `${((onLeave / current.headcount) * 100).toFixed(1)}%` : undefined }]
            : []),
          ...(directory ? [{ key: "new-joinees", label: "New joinees", value: formatNumber(newJoinees), hint: "this month" }] : []),
          { key: "working-days", label: "Working days", value: formatNumber(current.working_days) },
          { key: "holidays", label: "Holidays", value: formatNumber(current.holidays), hint: "this month" },
          ...(attendance ? [{ key: "overtime", label: "Overtime hours", value: formatNumber(current.total_overtime_hours, 1) }] : []),
        ]}
      />
    </div>
  );
}

// -------------------------------------------------------------- attendance

export function PresentMonthWidget(_: WidgetProps) {
  const q = useDashboardReport();
  const current = q.data?.current;
  const present = current?.attendance_mix?.present ?? 0;
  return (
    <KpiWidget
      label="Present this month"
      icon="check-circle"
      tone="emerald"
      to="/attendance"
      state={stateOf(q)}
      value={current ? formatNumber(present) : undefined}
      hint={current?.headcount ? `${((present / current.headcount) * 100).toFixed(0)}% of workforce` : undefined}
    />
  );
}

export function AttendanceMixWidget(_: WidgetProps) {
  const q = useDashboardReport();
  return (
    <WidgetCard title="Attendance Mix" state={stateOf(q)} action={<ViewAllLink to="/attendance" />}>
      <AttendanceDonut mix={q.data?.current.attendance_mix ?? {}} />
    </WidgetCard>
  );
}

export function OvertimeWidget(_: WidgetProps) {
  const q = useDashboardReport();
  return (
    <KpiWidget
      label="Overtime hours"
      icon="clock"
      tone="amber"
      to="/attendance"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.current.total_overtime_hours, 1) : undefined}
      hint="this month"
    />
  );
}

type RosterKey = "present" | "absent" | "late" | "not_marked";

function useTodayCount(key: RosterKey): { state: DataState; value: string | undefined; hint?: string } {
  const q = useHrToday();
  const roster = q.data?.attendance_roster;
  // The roster is the canonical day headcount; an older backend without it
  // falls back to the plain per-status counts.
  const raw = roster ? roster[key] : key === "not_marked" ? undefined : q.data?.attendance_today?.[key] ?? (q.data ? 0 : undefined);
  return {
    state: stateOf(q),
    value: raw === undefined ? undefined : formatNumber(raw),
    hint: roster ? `of ${formatNumber(roster.expected)} expected` : undefined,
  };
}

export function TodayPresentWidget(_: WidgetProps) {
  const { state, value, hint } = useTodayCount("present");
  return <KpiWidget label="Present today" icon="check-circle" tone="emerald" to="/attendance" state={state} value={value} hint={hint} />;
}

export function TodayAbsentWidget(_: WidgetProps) {
  const { state, value, hint } = useTodayCount("absent");
  return <KpiWidget label="Absent today" icon="x-circle" tone="accent" to="/attendance" state={state} value={value} hint={hint} />;
}

export function TodayLateWidget(_: WidgetProps) {
  const { state, value, hint } = useTodayCount("late");
  return <KpiWidget label="Late check-ins today" icon="clock" tone="amber" to="/attendance" state={state} value={value} hint={hint} />;
}

export function TodayNotMarkedWidget(_: WidgetProps) {
  const { state, value, hint } = useTodayCount("not_marked");
  return <KpiWidget label="Missing check-in today" icon="alert-triangle" tone="amber" to="/attendance" state={state} value={value} hint={hint} />;
}

export function TodayRosterWidget(_: WidgetProps) {
  const q = useHrToday();
  const r = q.data?.attendance_roster;
  return (
    <SummaryWidget
      title="Today's Attendance"
      state={stateOf(q)}
      link={{ to: "/attendance" }}
      rows={
        r && [
          { label: "Expected today", value: formatNumber(r.expected) },
          { label: "Present", value: formatNumber(r.present), tone: "good" },
          { label: "Late", value: formatNumber(r.late), tone: r.late > 0 ? "warn" : "default" },
          { label: "Half day", value: formatNumber(r.half_day) },
          { label: "Absent", value: formatNumber(r.absent), tone: r.absent > 0 ? "warn" : "default" },
          { label: "On leave", value: formatNumber(r.on_leave) },
          { label: "Not marked yet", value: formatNumber(r.not_marked), tone: r.not_marked > 0 ? "warn" : "default" },
          { label: "Field work", value: formatNumber(r.field_work) },
        ]
      }
    />
  );
}

export function GeoFencingWidget(_: WidgetProps) {
  const q = useGeoSnapshot();
  if (q.isLoading || q.isError || !q.data) {
    return (
      <WidgetCard title="Geo-Fencing" state={stateOf(q)}>
        <NoAccess what="Geo-fencing" />
      </WidgetCard>
    );
  }
  return (
    <div className="h-full [&>div]:h-full">
      <GeoSnapshotCard snapshot={q.data} interactive />
    </div>
  );
}

// ------------------------------------------------------------------- leave

export function PendingLeavesWidget(_: WidgetProps) {
  const q = useDashboardReport();
  return (
    <KpiWidget
      label="Pending leave requests"
      icon="calendar-check"
      tone="amber"
      to="/leaves"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.current.pending_leave_requests) : undefined}
      hint="this month"
    />
  );
}

export function OnLeaveTodayWidget(_: WidgetProps) {
  const q = useHrToday();
  return (
    <KpiWidget
      label="On leave today"
      icon="calendar"
      tone="indigo"
      to="/leaves"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.on_leave_today) : undefined}
    />
  );
}

export function RecentLeavesWidget(_: WidgetProps) {
  const q = useRecentLeaves();
  if (q.isLoading || q.isError) {
    return (
      <WidgetCard title="Leave Applications" state={stateOf(q)}>
        {null}
      </WidgetCard>
    );
  }
  return (
    <div className="h-full min-w-0 [&>div]:h-full">
      <LeaveApplicationsCard items={q.data?.items} className="h-full" />
    </div>
  );
}

// --------------------------------------------------------------- payroll

export function PayrollTotalWidget(_: WidgetProps) {
  const q = useDashboardReport();
  const payroll = q.data?.previous?.payroll;
  return (
    <KpiWidget
      label="Total Payroll"
      icon="wallet"
      tone="indigo"
      to="/payroll/runs"
      state={stateOf(q)}
      value={q.data ? (payroll ? formatMoneyCompact(payroll.total_gross) : "No run yet") : undefined}
      hint={payroll && q.data?.previous ? formatPeriod(q.data.previous.period) : undefined}
    />
  );
}

export function PayrollStatusWidget(_: WidgetProps) {
  const q = useDashboardReport();
  const previous = q.data?.previous;
  if (q.isLoading || q.isError || !previous?.payroll) {
    return (
      <WidgetCard title="Payroll" state={stateOf(q)} action={<ViewAllLink to="/payroll/runs" label="Payroll runs →" />}>
        <p className="py-6 text-center text-sm text-slate-400">No payroll run to show yet.</p>
      </WidgetCard>
    );
  }
  return (
    <div className="h-full [&>div]:h-full">
      <PayrollSnapshotCard payroll={previous.payroll} period={previous.period} />
    </div>
  );
}

// ------------------------------------------------------------ expenses

export function ExpenseTotalWidget(_: WidgetProps) {
  const q = useExpenseSummary();
  const s = q.data;
  return (
    <KpiWidget
      label="Total Expense"
      icon="credit-card"
      tone="accent"
      to="/expenses/dashboard"
      state={stateOf(q)}
      value={s ? formatMoneyCompact(s.total_amount) : undefined}
      hint={s ? `${s.total_claims} claim${s.total_claims === 1 ? "" : "s"}` : undefined}
      sparkline={s?.monthly_trend.map((p) => Number(p.amount))}
    />
  );
}

export function ExpenseSummaryWidget(_: WidgetProps) {
  const q = useExpenseSummary();
  const s = q.data;
  return (
    <SummaryWidget
      title="Expense Claims"
      state={stateOf(q)}
      link={{ to: "/expenses/dashboard" }}
      rows={
        s && [
          { label: "Total claims", value: formatNumber(s.total_claims) },
          { label: "Awaiting approval", value: formatNumber(s.pending_approval.count), tone: s.pending_approval.count > 0 ? "warn" : "default" },
          { label: "Approved", value: formatNumber(s.approved.count), tone: "good" },
          { label: "Pending reimbursement", value: formatMoneyCompact(s.pending_reimbursement), tone: Number(s.pending_reimbursement) > 0 ? "warn" : "default" },
        ]
      }
    />
  );
}

// -------------------------------------------------------------- activity

export function RecentActivityWidget(_: WidgetProps) {
  const q = useRecentActivity();
  return (
    <WidgetCard title="Recent Activity" state={stateOf(q)}>
      <RecentActivities items={q.data?.items ?? []} />
    </WidgetCard>
  );
}

export function TicketsWidget(_: WidgetProps) {
  const q = useHrToday();
  return (
    <KpiWidget
      label="Open helpdesk tickets"
      icon="help-circle"
      tone="primary"
      to="/helpdesk/tickets"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.open_helpdesk_tickets) : undefined}
    />
  );
}
